import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import closedEye from "assets/icons/closedEye.svg";
import openedEye from "assets/icons/openedEye.svg";
import classnames from "classnames";
import {
  Controller,
  type FieldError,
  type FieldErrors,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage } from "@hookform/error-message";
import { Autocomplete, Box, FormGroup, TextField } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import CatalogItemActionsComponent from "../../features/Catalogs/CatalogItemActionsComponent";
import ConfirmationModal from "../../features/ConfirmationModal";
import FormButtons from "../../features/FormButtons";
import { useAppDispatch, useAppSelector, useSetServerError } from "../../hooks";
import { countries } from "../../models/countries";
import {
  ICreateUserFormInput,
  IFormInput,
  IUserPostArgs,
} from "../../models/user";
import { createUser, getUsers } from "../../store";
import { autocompleteStyles } from "../../styles/MUIStyles";
import { StyledSelect, StyledSelectError } from "../../ui/Select";
import { sendNotification } from "../../ui/Toast";

import styles from "../Catalogs/CatalogsLayout.module.scss";
import style from "./CreateUserForm.module.scss";

const CreateUserForm: FC<{
  createOrUpdate: string | null;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  isDrawerOpen: boolean;
  setCreateOrUpdate: Dispatch<SetStateAction<string | null>>;
  debouncedSearchUserName: string;
  debouncedSearchUserEmail: string;
  pageNumber: number;
  pageSize: number;
  userStatus: string;
  sortConfig: { column: string | null; direction: string | null };
}> = ({
  createOrUpdate,
  setIsDrawerOpen,
  isDrawerOpen,
  setCreateOrUpdate,
  debouncedSearchUserName,
  debouncedSearchUserEmail,
  pageNumber,
  pageSize,
  userStatus,
  sortConfig,
}): JSX.Element => {
    const { t } = useTranslation();


    const dispatch = useAppDispatch();
    const currentLanguage = localStorage.getItem("i18nextLng");

    const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

    const countries = useAppSelector((state) => state.catalogs.countries);
    const userRoles = useAppSelector((state) => state.users.roles);
    const userErrors = useAppSelector((state) => state.users.userErrors);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [isPasswordVisibleConfirm, setIsPasswordVisibleConfirm] =
      useState(false);

    const initValues: ICreateUserFormInput = {
      email: "",
      firstName: "",
      middleName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
      roleNames: [],
      phoneNumber: "",
      countryId: "",
    };

    const {
      register,
      handleSubmit,
      setValue,
      getValues,
      setError,
      formState: { errors },
      clearErrors,
      control,
      watch,
      reset,
    } = useForm<IUserPostArgs>({
      mode: "onSubmit",
    });

    const fieldRefs = {
      email: useRef<HTMLInputElement | null>(null),
      firstName: useRef<HTMLInputElement | null>(null),
      middleName: useRef<HTMLInputElement | null>(null),
      lastName: useRef<HTMLInputElement | null>(null),
      password: useRef<HTMLInputElement | null>(null),
      confirmPassword: useRef<HTMLInputElement | null>(null),
      phoneNumber: useRef<HTMLInputElement | null>(null),
      countryId: useRef<HTMLDivElement | null>(null),
      roleNames: useRef<HTMLDivElement | null>(null),
    };



    const onSubmit: SubmitHandler<any> = async (formData: any) => {
      const response = await dispatch(
        createUser({
          email: formData.email,
          firstName: formData.firstName,
          middleName: formData.middleName,
          lastName: formData.lastName,
          password: formData.password,
          roleNames: formData.roleNames,
          phoneNumber: formData.phoneNumber,
          countryId: formData.countryId,
        }),
      );

      if (response.meta.requestStatus === "fulfilled") {
        sendNotification(t("notifications.successfullyCreated"), "success");
        reset(initValues);
        clearErrors();
        setCreateOrUpdate(null);

        await dispatch(
          getUsers({
            userName: debouncedSearchUserName,
            email: debouncedSearchUserEmail,
            pageNumber,
            userStatus,
            pageSize,
            sortBy: sortConfig,
          }),
        );
        setIsDrawerOpen(false);
        clearErrors();
      }
      return;
    };

    const setFormData = (
      fields: IFormInput | null,
      operation: string | null,
      state: string | null,
    ) => {
      setValue("email", fields?.email || "");
      setValue("firstName", fields?.firstName || "");
      setValue("middleName", fields?.middleName || "");
      setValue("lastName", fields?.lastName || "");
      setValue("password", fields?.password || "");
      setValue("roleNames", fields?.roleNames || []);
      setValue("phoneNumber", fields?.phoneNumber || "");
      setValue("countryId", fields?.countryId || "");
      state && setIsDrawerOpen((prevState) => !prevState);
    };

    const formClose = () => {
      if (formChangesChecking()) setConfirmationModalOpen(true);
      else closeFormDrawer();
      clearErrors();
    };

    const closeFormDrawer = () => {
      // reset(initValues);
      reset({ ...initValues, password: "", confirmPassword: "" });
      setCreateOrUpdate(null);
      setIsDrawerOpen(false);
      clearErrors();
    };

    const closeModalConfirm = () => {
      // reset(initValues);
      reset({ ...initValues, password: "", confirmPassword: "" });
      setIsDrawerOpen(false);
      setConfirmationModalOpen(false);
      clearErrors();
    };

    // returns true, if form was updated
    const formChangesChecking = () => {
      return Boolean(
        getValues("email") ||
        getValues("firstName") ||
        getValues("middleName") ||
        getValues("lastName") ||
        getValues("password") ||
        getValues("roleNames") ||
        getValues("phoneNumber") ||
        getValues("countryId"),
      );
    };

    useEffect(() => {
      setFormData(null, null, null);
    }, [dispatch, t]);

    useSetServerError({
      serverError: userErrors,
      setError,
      clearErrors,
      errorNames: [
        "email",
        "firstName",
        "middleName",
        "lastName",
        "password",
        "roleName",
        "phoneNumber",
        "countryId",
      ],
    });

    useEffect(() => {
      const errorOrder: (keyof typeof fieldRefs)[] = [
        "email",
        "firstName",
        "middleName",
        "lastName",
        "password",
        "confirmPassword",
        "phoneNumber",
        "countryId",
        "roleNames",
      ];
      for (const key of errorOrder) {
        if (errors[key] && fieldRefs[key].current) {
          fieldRefs[key].current!.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
          break;
        }
      }
    }, [errors]);

    const hasPasswordError = !!errors?.password;
    const hasConfirmPasswordError = !!errors?.confirmPassword;

    const getClassNames = (error: FieldError | undefined): string => {
      return error ? styles.catalogsDrawerInputError : styles.catalogsDrawerInput;
    };

    const filterOptions = (options: any, { inputValue }: any) => {
      const lang = currentLanguage === "en-US" ? "en" : "ru";
      const normalizedInput = inputValue.toLowerCase();
      return [...options]
        .filter((opt) => {
          const label = opt.name;
          return label.toLowerCase().includes(normalizedInput);
        })
        .sort((a, b) => {
          const aStarts = a.name.toLowerCase().startsWith(normalizedInput);
          const bStarts = b.name.toLowerCase().startsWith(normalizedInput);

          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;

          return a.name.localeCompare(b.name, lang, {
            sensitivity: "base",
          });
        });
    };

    return (
      <>
        <CatalogItemActionsComponent
          name="users"
          isDrawerOpen={isDrawerOpen}
          createOrUpdate={createOrUpdate}
          closeFormDrawer={formClose}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.formInputWrapper}>
              <label htmlFor="email" className={styles.drawerTopSubTitle}>
                {t("data.email")}
              </label>
              <div>
                <input
                  {...register("email", {
                    required: t("validation.requiredField") as string,
                    maxLength: {
                      value: 256,
                      message: t("validation.maxLength256"),
                    },
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i,
                      message: t("validation.invalidEmail") as string,
                    },
                  })}
                  ref={(el) => {
                    register("email").ref(el);
                    fieldRefs.email.current = el;
                  }}
                  id="email"
                  type="text"
                  autoComplete="off"
                  placeholder={t("data.email") as string}
                  className={getClassNames(errors.email)}
                />
                <ErrorMessage
                  name="email"
                  errors={errors}
                  render={({ message }) => <span>{message}</span>}
                />
              </div>
              <label htmlFor="firstName" className={styles.drawerTopSubTitle}>
                {t("data.firstName")}
              </label>
              <div>
                <input
                  {...register("firstName", {
                    required: t("validation.requiredField") as string,
                    maxLength: {
                      value: 50,
                      message: t("validation.maxLength50"),
                    },
                    pattern: {
                      value: /^[А-ЯЁA-Z]+$/i,
                      message: t("validation.onlyLetters") as string,
                    },
                  })}
                  ref={(el) => {
                    register("firstName").ref(el);
                    fieldRefs.firstName.current = el;
                  }}
                  id="firstName"
                  type="text"
                  autoComplete="off"
                  placeholder={t("data.firstName") as string}
                  className={getClassNames(errors.firstName)}
                />
                <ErrorMessage
                  name="firstName"
                  errors={errors}
                  render={({ message }) => <span>{message}</span>}
                />
              </div>
              <label htmlFor="middleName" className={styles.drawerTopSubTitle}>
                {t("data.middleName")}
              </label>
              <div>
                <input
                  {...register("middleName", {
                    required: t("validation.requiredField") as string,
                    maxLength: {
                      value: 50,
                      message: t("validation.maxLength50"),
                    },
                    pattern: {
                      value: /^[А-ЯЁA-Z]+$/i,
                      message: t("validation.onlyLetters") as string,
                    },
                  })}
                  ref={(el) => {
                    register("middleName").ref(el);
                    fieldRefs.middleName.current = el;
                  }}
                  id="middleName"
                  type="text"
                  autoComplete="off"
                  placeholder={t("data.middleName") as string}
                  className={getClassNames(errors.middleName)}
                />
                <ErrorMessage
                  name="middleName"
                  errors={errors}
                  render={({ message }) => <span>{message}</span>}
                />
              </div>
              <label htmlFor="lastName" className={styles.drawerTopSubTitle}>
                {t("data.lastName")}
              </label>
              <div>
                <input
                  {...register("lastName", {
                    required: t("validation.requiredField") as string,
                    maxLength: {
                      value: 50,
                      message: t("validation.maxLength50"),
                    },
                    pattern: {
                      value: /^[А-ЯЁA-Z]+$/i,
                      message: t("validation.onlyLetters") as string,
                    },
                  })}
                  ref={(el) => {
                    register("lastName").ref(el);
                    fieldRefs.lastName.current = el;
                  }}
                  id="lastName"
                  type="text"
                  autoComplete="off"
                  placeholder={t("data.lastName") as string}
                  className={getClassNames(errors.lastName)}
                />
                <ErrorMessage
                  name="lastName"
                  errors={errors}
                  render={({ message }) => <span>{message}</span>}
                />
              </div>

              <label htmlFor="password" className={styles.drawerTopSubTitle}>
                {t("data.password")}
              </label>
              <div className={styles.loginFormInnerPasswordContainer}>
                <input
                  {...register("password", {
                    required: t("validation.requiredField") as string,
                    maxLength: {
                      value: 50,
                      message: t("validation.maxLength50"),
                    },
                    minLength: {
                      value: 8,
                      message: t("validation.minLength8"),
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
                      message: t("validation.mustIncludeComplexity"),
                    },
                  })}
                  ref={(el) => {
                    register("password").ref(el);
                    fieldRefs.password.current = el;
                  }}
                  id="password"
                  type={isPasswordVisible ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder={t("data.password") as string}
                  className={getClassNames(errors.password)}
                />
                {isPasswordVisible ? (
                  <img
                    className={classnames(styles.loginFormInnerPasswordEye, {
                      [styles.loginFormInnerPasswordEyeError]: hasPasswordError,
                    })}
                    src={openedEye}
                    onClick={() =>
                      setIsPasswordVisible((prevState) => !prevState)
                    }
                    alt="password eye"
                  />
                ) : (
                  <img
                    className={classnames(styles.loginFormInnerPasswordEye, {
                      [styles.loginFormInnerPasswordEyeError]: hasPasswordError,
                    })}
                    src={closedEye}
                    onClick={() =>
                      setIsPasswordVisible((prevState) => !prevState)
                    }
                    alt="password eye"
                  />
                )}
              </div>
              <ErrorMessage
                name="password"
                errors={errors}
                render={({ message }) => (
                  <span className={styles.errorMessage}>{message}</span>
                )}
              />
              <label
                htmlFor="confirmPassword"
                className={styles.drawerTopSubTitle}
              >
                {t("data.confirmPassword")}
              </label>
              <div className={styles.loginFormInnerPasswordContainer}>
                <input
                  {...register("confirmPassword", {
                    required: t("validation.requiredField") as string,
                    maxLength: {
                      value: 50,
                      message: t("validation.maxLength50"),
                    },
                    validate: (value) =>
                      value === getValues("password") ||
                      (t("validation.passwordsDoNotMatch") as string),
                  })}
                  ref={(el) => {
                    register("confirmPassword").ref(el);
                    fieldRefs.confirmPassword.current = el;
                  }}
                  id="confirmPassword"
                  type={isPasswordVisibleConfirm ? "text" : "password"}
                  style={{ paddingRight: "40px" }}
                  autoComplete="off"
                  aria-autocomplete="none"
                  autoCorrect="none"
                  placeholder={t("data.confirmPassword") as string}
                  className={getClassNames(errors.confirmPassword)}
                />
                {isPasswordVisibleConfirm ? (
                  <img
                    className={classnames(styles.loginFormInnerPasswordEye, {
                      [styles.loginFormInnerPasswordEyeError]:
                        hasConfirmPasswordError,
                    })}
                    src={openedEye}
                    onClick={() =>
                      setIsPasswordVisibleConfirm((prevState) => !prevState)
                    }
                    alt="password eye"
                  />
                ) : (
                  <img
                    className={classnames(styles.loginFormInnerPasswordEye, {
                      [styles.loginFormInnerPasswordEyeError]:
                        hasConfirmPasswordError,
                    })}
                    src={closedEye}
                    onClick={() =>
                      setIsPasswordVisibleConfirm((prevState) => !prevState)
                    }
                    alt="password eye"
                  />
                )}
              </div>
              <ErrorMessage
                name="confirmPassword"
                errors={errors}
                render={({ message }) => (
                  <span className={styles.errorMessage}>{message}</span>
                )}
              />
              <label htmlFor="phoneNumber" className={styles.drawerTopSubTitle}>
                {t("data.phoneNumber")}
              </label>
              <div>
                <input
                  {...register("phoneNumber", {
                    required: t("validation.requiredField") as string,
                    maxLength: {
                      value: 50,
                      message: t("validation.maxLength50"),
                    },
                  })}
                  ref={(el) => {
                    register("phoneNumber").ref(el);
                    fieldRefs.phoneNumber.current = el;
                  }}
                  id="phoneNumber"
                  type="text"
                  autoComplete="off"
                  placeholder={t("data.phoneNumber") as string}
                  className={getClassNames(errors.phoneNumber)}
                />
                <ErrorMessage
                  name="phoneNumber"
                  errors={errors}
                  render={({ message }) => <span>{message}</span>}
                />
              </div>
              <FormControl
                fullWidth
                sx={{ marginBottom: "1rem" }}
                ref={fieldRefs.countryId}
              >
                <span className={styles.drawerTopSubTitle}>
                  {t("data.country")}
                </span>
                <Controller
                  name="countryId"
                  defaultValue=""
                  control={control}
                  rules={{
                    required:
                      createOrUpdate === "create"
                        ? (t("validation.requiredField") as string)
                        : false,
                  }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      ListboxProps={{
                        style: { maxHeight: "200px" },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          sx={autocompleteStyles}
                          placeholder={t("data.country") || ""}
                          error={!!fieldState.error}
                        />
                      )}
                      options={[...countries].sort(
                        (a: countries, b: countries) => {
                          return a.name.localeCompare(
                            b.name,
                            currentLanguage === "en-US" ? "en" : "ru",
                          );
                        },
                      )}
                      filterOptions={filterOptions}
                      getOptionLabel={(option) => option.name}
                      onChange={(event, value: countries | null) =>
                        field.onChange(value?.id)
                      }
                      value={
                        countries.filter(
                          (el) => el?.id === getValues("countryId"),
                        )[0] || null
                      }
                    />
                  )}
                />
                <ErrorMessage
                  name="countryId"
                  errors={errors}
                  render={({ message }) => (
                    <span className={styles.selectErrorMsg}>{message}</span>
                  )}
                />
              </FormControl>
              <FormControl
                fullWidth
                sx={{ marginBottom: "1rem" }}
                ref={fieldRefs.roleNames}
              >
                <span className={styles.drawerTopSubTitle}>
                  {t("data.roleName")}
                </span>
                <Controller
                  name="roleNames"
                  control={control}
                  defaultValue={[]}
                  rules={{
                    required:
                      createOrUpdate === "create"
                        ? (t("validation.requiredField") as string)
                        : false,
                    validate: (value) =>
                      (value && value.length > 0) ||
                      (t("validation.requiredField") as string),
                  }}
                  render={({ field }) => (
                    <FormGroup className={style.roleFormGroup}>
                      {userRoles.map((role) => (
                        <label
                          key={role.roleName}
                          className={style.roleCheckboxWrapper}
                        >
                          <input
                            type="checkbox"
                            value={role.roleName}
                            checked={field.value.includes(role.roleName)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                field.onChange([...field.value, role.roleName]);
                              } else {
                                field.onChange(
                                  field.value.filter((r) => r !== role.roleName),
                                );
                              }
                            }}
                          />
                          {t(`roles.${role.roleName}`)}
                        </label>
                      ))}
                    </FormGroup>
                  )}
                />
                <Box>
                  <ErrorMessage
                    name="roleNames"
                    errors={errors}
                    render={({ message }) => (
                      <span
                        className={styles.selectErrorMsg}
                        style={{ color: "red", marginTop: 4, display: "block" }}
                      >
                        {message || t("validation.requiredRole")}
                      </span>
                    )}
                  />
                </Box>
              </FormControl>
            </div>
            <FormButtons
              isLoading={false}
              createOrUpdate={createOrUpdate}
              cancelHandler={() => formClose()}
            />
          </form>
        </CatalogItemActionsComponent>
        <ConfirmationModal
          isModalOpen={isConfirmationModalOpen}
          setIsModalOpen={setConfirmationModalOpen}
          actionName={"Close"}
          instanceName=""
          message={"closeFormWithoutSaving"}
          handleAction={() => closeModalConfirm()}
        />
      </>
    );
  };

export default CreateUserForm;
